import { useEffect, useState } from 'react';
import { Configuration, PromotionsApi } from '@/api';
import { EmptyResult } from '@/models/Pagination';
import { Promotion, Promotions } from '@/models/Promotion';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { setPaginationResultIfNotLoading } from '@/utils/pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState = EmptyResult<Promotion>();

interface UseFindPromotionsQueryResult {
  promotions: Promotions;
  isLoading: boolean;
  error: Error | null;
  itemsPerPage: number;
  invalidateQuery: () => Promise<void>;
}

interface PromotionFilters {
  supplierId: string | string[];
}

const useFindPromotionsQuery = (
  page: number,
  searchQuery?: string,
  filters?: Partial<PromotionFilters>,
): UseFindPromotionsQueryResult => {
  const queryClient = useQueryClient();
  const [promotions, setPromotions] = useState<Promotions>(initialState);

  const promotionsApi = new PromotionsApi(new Configuration(useAuth));

  const itemsPerPage = 10;

  const supplierId = filters?.supplierId;

  const { isLoading, data, isFetching, error } = useQuery({
    queryKey: ['promotions', page, searchQuery, supplierId],
    queryFn: () =>
      promotionsApi.findPromotions(
        searchQuery,
        filters,
        page * itemsPerPage - itemsPerPage,
        itemsPerPage,
      ),
  });

  useEffect(() => {
    setPaginationResultIfNotLoading(data, setPromotions, isLoading);
  }, [data, isLoading, isFetching]);

  const invalidateQuery = async () => {
    return queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'promotions',
    });
  };

  return {
    promotions,
    isLoading,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindPromotionsQuery;
